/**
 * Freshness guard pra dados que dependem de cron periodico (Task #73).
 *
 * Aplicado primeiro na coluna POSICAO ABERTA da tabela "Saldo projetado —
 * proximos 6 meses" (depende do cron zeus_volume_total criado em #96). UI
 * renderiza badges conforme idade do `last_sync` retornado pelo backend.
 *
 * Bandas (Veiga 30/04 — em sync com o ZeusSyncBadge global):
 *   < 26h          → "fresh" (sem aviso)
 *   26h ≤ x < 72h  → "stale" (badge ambar, valor com opacidade reduzida)
 *   ≥ 72h ou null  → "error" (badge vermelha, valor com opacidade reduzida)
 *
 * Em qualquer estado o valor renderiza — usuario precisa enxergar que esta
 * ruim, nao que sumiu (decisao Veiga).
 */

export type FreshnessState = "fresh" | "stale" | "error";

export const STALE_THRESHOLD_HOURS = 26;
export const ERROR_THRESHOLD_HOURS = 72;

/** Devolve idade em horas entre `lastSync` ISO e `agora` (default Date.now).
 * null quando lastSync invalido/null — chamador trata como "error". */
export function hoursSince(lastSync: string | null, agora: Date = new Date()): number | null {
  if (!lastSync) return null;
  const t = Date.parse(lastSync);
  if (Number.isNaN(t)) return null;
  return (agora.getTime() - t) / 3_600_000;
}

/** Classifica o estado do dado baseado em lastSync ISO + thresholds. */
export function classifyFreshness(
  lastSync: string | null,
  agora: Date = new Date(),
): FreshnessState {
  const hours = hoursSince(lastSync, agora);
  if (hours === null) return "error";
  if (hours >= ERROR_THRESHOLD_HOURS) return "error";
  if (hours >= STALE_THRESHOLD_HOURS) return "stale";
  return "fresh";
}

/** Texto humano pra "Xh atras" / "Xd atras" / "—". Usado no badge. */
export function formatHoursAgo(hours: number | null): string {
  if (hours === null) return "—";
  if (hours < 1) {
    const mins = Math.max(1, Math.round(hours * 60));
    return `${mins}min atras`;
  }
  if (hours < 48) return `${Math.round(hours)}h atras`;
  return `${Math.floor(hours / 24)}d atras`;
}
